import {attr, controller, target, targets} from '@github/catalyst'
import {get, parseRequestOptionsFromJSON, supported} from '@github/webauthn-json/browser-ponyfill'
import type {CredentialRequestOptionsJSON} from '@github/webauthn-json/browser-ponyfill'
import {requestSubmit} from '@github-ui/form-utils'
import {isFeatureEnabled} from '@github-ui/feature-flags'

// An event dispatched by `WebauthnGetElement` when a normal get request is initiated
const webauthnGetEvent = 'webauthn-get-prompt'
// An event dispatched by `WebauthnSubtleElement` when its button is submitted
const webauthnSubtleEvent = 'webauthn-subtle-submit'

// eslint-disable-next-line wc/no-exports-with-element
export const State = {
  Initializing: 'initializing',
  Unsupported: 'unsupported',
  Ready: 'ready',
  Waiting: 'waiting',
  Error: 'error',
  Submitting: 'submitting',
} as const

export type State = (typeof State)[keyof typeof State]

@controller('webauthn-get')
export class WebauthnGetElement extends HTMLElement {
  state: State = State.Initializing
  @target declare button: HTMLButtonElement
  @target declare buttonText: HTMLElement
  // `messages` contains all the message elements.
  @targets declare messages: HTMLElement[]
  @target declare capitalizedDescription: HTMLElement
  @target declare unsupportedMessage: HTMLElement
  @target declare passkeysUnsupportedMessage: HTMLElement
  @target declare waitingMessage: HTMLElement
  @target declare errorMessage: HTMLElement
  @target declare errorText: HTMLElement

  static attrPrefix = ''
  @attr dataJson = ''
  @attr subtleLogin = false
  declare private originalButtonText: string
  private hasErrored = false
  declare private originalErrorText: string | null
  declare private passkeySupport: boolean

  async connectedCallback() {
    this.originalButtonText = this.getCurrentButtonText()
    this.originalErrorText = this.errorText.textContent
    this.setState(supported() ? State.Ready : State.Unsupported)
    this.passkeySupport = await window.PublicKeyCredential?.isUserVerifyingPlatformAuthenticatorAvailable()
    // show the passkey unsupported message if it's present on the page (renders when the user has passkeys)
    // and we're not already showing the webauthn unsupported message
    if (this.state !== State.Unsupported && !this.passkeySupport && this.passkeysUnsupportedMessage) {
      this.passkeysUnsupportedMessage.hidden = false
    }
    // if this get request is for login and the user doesn't have any passkeys
    if (this.subtleLogin) {
      this.handleWebauthnSubtle()
    } else {
      this.showWebauthnLoginFragment()
    }
  }

  // Handles login specific webauthn-get behavior.
  // Reveals and prepares webauthn-subtle behavior for the subtle webauthn login scenario.
  handleWebauthnSubtle(): void {
    // show the subtle UX. webauthn-get loads last so we don't need to worry about if it isn't already on the page
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const subtle = document.querySelector<HTMLElement>('.js-webauthn-subtle')!
    if (!subtle) {
      return
    }

    subtle.hidden = false
    this.updateWebauthnSubtleParentBoxVisibility(false)
    subtle.addEventListener(webauthnSubtleEvent, () => {
      this.showWebauthnLoginFragment()
      // if user tries subtle passkey login from an unsupported browser, show the unsupported UX but don't trigger the prompt
      if (this.state !== State.Unsupported) {
        this.prompt()
      }
    })
  }

  // Handles login specific webauthn-get behavior.
  // Reveals main webauthn login component and hides the webauthn subtle hint - used for the subtle:false login scenario
  // and for switching from subtle over to the main passkey login UX after user input.
  showWebauthnLoginFragment(): void {
    // grab and reveal parent webauthn section that isn't a catalyst component
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const loginFragment = document.querySelector<HTMLElement>('.js-webauthn-login-section')!
    if (!loginFragment) {
      return
    }

    loginFragment.hidden = false

    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const login_divider = document.querySelector<HTMLElement>('.js-webauthn-login-divider')!
    if (!login_divider) {
      return
    }

    login_divider.hidden = false

    // also hide the parent box for the webauthn subtle component
    // this is nessesary when it's the only content in the login alternatives box for subtle:false logins
    this.updateWebauthnSubtleParentBoxVisibility(true)
  }

  updateWebauthnSubtleParentBoxVisibility(hidden: boolean): void {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    const parentDiv = document.querySelector<HTMLElement>('.js-webauthn-hint')!
    if (!parentDiv) {
      return
    }
    parentDiv.hidden = hidden
  }

  getCurrentButtonText(): string {
    return this.buttonText.textContent || ''
  }

  setCurrentButtonText(text: string): void {
    this.buttonText.textContent = text
  }

  setState(state: State): void {
    // Reset to defaults
    const retryMessage = this.button.getAttribute('data-retry-message') || this.originalButtonText
    const buttonText = this.hasErrored ? retryMessage : this.originalButtonText
    this.setCurrentButtonText(buttonText)
    this.button.disabled = false
    this.button.hidden = false
    this.errorText.textContent = ''
    for (const elem of this.messages) {
      elem.hidden = true
    }

    switch (state) {
      case State.Initializing:
        this.button.disabled = true
        break
      case State.Unsupported:
        this.button.disabled = true
        this.unsupportedMessage.hidden = false
        // hide the passkey specific error if we're showing the generic one
        if (this.passkeysUnsupportedMessage) {
          this.passkeysUnsupportedMessage.hidden = true
        }
        break
      case State.Ready:
        break
      case State.Waiting:
        this.waitingMessage.hidden = false
        this.button.hidden = true
        break
      case State.Error:
        this.errorMessage.hidden = false
        this.errorText.textContent = this.originalErrorText
        break
      case State.Submitting:
        this.setCurrentButtonText('Verifying…')
        this.button.disabled = true
        break
      default:
        throw new Error('invalid state')
    }

    this.state = state
  }

  // silent_unless_success: don't show waiting or error status. This is for automatically attempting a prompt at
  // page/modal load time, without showing confusing UI if the browser rejects the attempt due to a missing user
  // gesture. Most browsers allow at least one such prompt per page load, but we can't rely on it. In theory we could
  // try to show an error to the user depending on the `get` rejection, but the spec is still in flux and browsers
  // constantly change their mind (and have bugs). So we err on the side of not showing an error.
  async prompt(event?: Event, silent_unless_success?: boolean): Promise<void> {
    event?.preventDefault() // prevent default page form submission
    this.dispatchEvent(new CustomEvent(webauthnGetEvent))
    try {
      if (!silent_unless_success) {
        this.setState(State.Waiting)
      }

      if (isFeatureEnabled('migrate_away_from_webauthn_json')) {
        const json: PublicKeyCredentialRequestOptionsJSON = JSON.parse(this.dataJson).publicKey
        const publicKey = PublicKeyCredential.parseRequestOptionsFromJSON(json)
        const signResponse = (await navigator.credentials.get({publicKey})) as PublicKeyCredential
        this.setState(State.Submitting)

        // Each `<webauthn-get>` element is currently embedded in a form (not
        // the other way around), so we have to query for the form outside of it.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const form = this.closest<HTMLFormElement>('.js-webauthn-form')!
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const responseField = form.querySelector<HTMLInputElement>('.js-webauthn-response')!
        responseField.value = JSON.stringify(signResponse.toJSON())
        requestSubmit(form)
      } else {
        const signRequest: CredentialRequestOptionsJSON = JSON.parse(this.dataJson)
        const options = parseRequestOptionsFromJSON(signRequest)
        const signResponse = await get(options)
        this.setState(State.Submitting)

        // Each `<webauthn-get>` element is currently embedded in a form (not
        // the other way around), so we have to query for the form outside of it.
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const form = this.closest<HTMLFormElement>('.js-webauthn-form')!
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const responseField = form.querySelector<HTMLInputElement>('.js-webauthn-response')!
        responseField.value = JSON.stringify(signResponse)
        requestSubmit(form)
      }
    } catch (error) {
      if (!silent_unless_success) {
        this.hasErrored = true
        this.setState(State.Error)
        throw error
      }
    }
  }
}
